import { BrowserQRCodeReader, type IScannerControls } from '@zxing/browser'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Lecteur de QR par la caméra arrière.
 *
 * Deux garde-fous appris des files d'attente :
 *
 * - **Anti-rebond** : la caméra décode le même QR ~10 fois par seconde. Sans
 *   verrou, un seul badge déclencherait dix appels réseau et dix bips.
 * - **Le flux ne s'arrête jamais** entre deux personnes : rallumer la caméra
 *   prend une à deux secondes, insupportable avec trente personnes derrière.
 */
export function QrScanner({
  onDetected,
  paused,
}: {
  onDetected: (token: string) => void
  paused: boolean
}) {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const controlsRef = useRef<IScannerControls | null>(null)
  const lastRef = useRef<{ token: string; at: number }>({ token: '', at: 0 })
  const pausedRef = useRef(paused)
  const onDetectedRef = useRef(onDetected)
  const [error, setError] = useState<string | null>(null)

  // Les refs évitent de relancer la caméra à chaque rendu du parent.
  pausedRef.current = paused
  onDetectedRef.current = onDetected

  useEffect(() => {
    let cancelled = false
    const reader = new BrowserQRCodeReader()

    void reader
      .decodeFromConstraints(
        { video: { facingMode: 'environment' } },
        videoRef.current ?? undefined,
        (result) => {
          if (!result || pausedRef.current) return

          const token = result.getText()
          const now = Date.now()
          // Même badge dans les 3 s : c'est la caméra qui insiste, pas la file.
          if (token === lastRef.current.token && now - lastRef.current.at < 3000) return

          lastRef.current = { token, at: now }
          onDetectedRef.current(token)
        },
      )
      .then((controls) => {
        if (cancelled) {
          controls.stop()
          return
        }
        controlsRef.current = controls
      })
      .catch(() => {
        if (!cancelled) setError(t('checkin.cameraDenied'))
      })

    return () => {
      cancelled = true
      controlsRef.current?.stop()
      controlsRef.current = null
    }
  }, [t])

  if (error) {
    return (
      <div className="flex aspect-square w-full items-center justify-center rounded-lg border border-line bg-bg-elevated p-6 text-center text-sm text-content-secondary">
        {error}
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden rounded-lg border border-line bg-black">
      <video
        ref={videoRef}
        className="aspect-square w-full object-cover"
        muted
        playsInline
        // iOS refuse la lecture automatique sans ces attributs.
        autoPlay
      />
      {/* Mire : sans repère visuel, on présente le badge de travers. */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="h-3/5 w-3/5 rounded-lg border-2 border-champagne-400/70" />
      </div>
      {paused ? <div className="absolute inset-0 bg-black/60" /> : null}
    </div>
  )
}
